import { Fragment, useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { errMsg, useAdminApi } from "@/lib/api";
import RemindersPanel from "@/components/admin/RemindersPanel";
import { AlertTriangle, FileText, Image as ImageIcon, MessageSquareWarning, Receipt } from "lucide-react";

const STATUSES = ["awaiting_payment", "payment_submitted", "unpaid", "paid", "confirmed", "completed", "cancelled", "expired"];
const LABEL = {
  awaiting_payment: "Awaiting payment", payment_submitted: "UPI - verify", unpaid: "Not paid", paid: "Paid online",
  confirmed: "Confirmed", completed: "Completed", cancelled: "Cancelled", expired: "Hold expired",
};

const NOTIFY_STATUSES = ["paid", "payment_submitted", "confirmed", "completed", "unpaid"];
const whatsappMissing = (b) => NOTIFY_STATUSES.includes(b.status) && !b.whatsapp_sent;

const toWaNum = (p) => {
  const d = String(p || "").replace(/\D/g, "");
  return d.length === 10 ? `91${d}` : d;
};
const nudgePatient = (b) => {
  const text = `Hi ${b.name}, we received your booking at Joyful Minds (Ref ${b.ref}) on ${b.date} at ${b.time}, but we haven't received the WhatsApp confirmation from your side. Please tap "Finish booking" on your booking page or send us a quick message here so the doctor can prepare for your appointment. Thank you.`;
  window.open(`https://wa.me/${toWaNum(b.phone)}?text=${encodeURIComponent(text)}`, "_blank");
};

function ProofViewer({ id }) {
  const call = useAdminApi();
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let u;
    call("get", `/admin/bookings/${id}/proof`, null, { responseType: "blob" })
      .then((blob) => { u = URL.createObjectURL(blob); setUrl(u); })
      .catch(() => toast.error("Could not load screenshot"));
    return () => { if (u) URL.revokeObjectURL(u); };
  }, [call, id]);
  if (!url) return <div className="h-40 animate-pulse rounded-xl bg-white/5" />;
  return (
    <a href={url} target="_blank" rel="noreferrer" data-testid="payment-proof-image">
      <img src={url} alt="Payment screenshot" className="max-h-80 w-full rounded-xl bg-black/30 object-contain" />
    </a>
  );
}

function BookingDetail({ b, onClose, onSaved }) {
  const call = useAdminApi();
  const [note, setNote] = useState(b.admin_note || "");
  const [showProof, setShowProof] = useState(false);
  const update = async (patch) => {
    try {
      onSaved(await call("patch", `/admin/bookings/${b.id}`, patch));
      toast.success("Saved");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const rows = [
    ["Ref", b.ref], ["Name", b.name], ["Age", b.age], ["Gender", b.gender], ["Address", b.address],
    ["WhatsApp", b.phone], ["Occupation / Education", b.occupation], ["Type of help", b.service_label],
    ["Date & time", `${b.date} ${b.time}-${b.end_time}`], ["Amount", `Rs ${b.amount}`],
    ["Payment", b.payment ? `${b.payment.method} · ${b.payment.ref}` : "-"], ["Language", b.lang],
    ["Consultation", b.consultation_completed ? "Completed" : "Pending"],
  ];
  const paymentDone = ["paid", "confirmed", "completed"].includes(b.status);
  const canReceipt = paymentDone && b.consultation_completed;
  const sendReceipt = async () => {
    try {
      const blob = await call("get", `/admin/bookings/${b.id}/receipt.jpg`, null, { responseType: "blob" });
      const digits = String(b.phone || "").replace(/\D/g, "");
      const num = digits.length === 10 ? `91${digits}` : digits;
      const text = `Hi ${b.name}, here is your digital receipt from Joyful Minds for your consultation on ${b.date} at ${b.time}. Ref: ${b.ref}. Thank you!`;
      const file = new File([blob], `JoyfulMinds-${b.ref}.jpg`, { type: "image/jpeg" });
      // Mobile: native share sheet opens WhatsApp directly with the receipt attached
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        try {
          await navigator.share({ files: [file], text, title: `Joyful Minds receipt - ${b.ref}` });
          return;
        } catch (e) {
          if (e.name === "AbortError") return;
        }
      }
      // Desktop fallback: auto-download the receipt and open the WhatsApp chat
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `JoyfulMinds-${b.ref}.jpg`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
      window.open(
        `https://wa.me/${num}?text=${encodeURIComponent(text + "\n\n(Receipt downloaded - please attach it in this chat.)")}`,
        "_blank",
      );
    } catch (e) {
      toast.error(errMsg(e, "Receipt is available only after both payment and consultation are marked completed"));
    }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="glass max-w-xl rounded-3xl text-emerald-50" data-testid="booking-detail-dialog">
        <DialogHeader><DialogTitle className="text-white">{b.name}</DialogTitle></DialogHeader>
        {b.status === "payment_submitted" && (
          <div className="flex items-start gap-2 rounded-xl border border-amber-300/50 bg-amber-300/15 p-3 text-sm text-amber-100" data-testid="unverified-callout">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-300" />
            <span>Payment not verified yet. Check Rs {b.amount} from {b.name} in your UPI app{b.payment?.ref && b.payment.ref !== "not provided" ? ` (transaction ID ${b.payment.ref})` : ""}{b.payment_proof ? " or view the screenshot below" : ""}, then press Mark payment completed.</span>
          </div>
        )}
        {b.status === "unpaid" && (
          <div className="flex items-start gap-2 rounded-xl border border-red-300/50 bg-red-500/15 p-3 text-sm text-red-100" data-testid="unpaid-callout">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-red-300" />
            <span>Patient chose to proceed without payment. Rs {b.amount} is still due - please follow up.</span>
          </div>
        )}
        {b.slot_conflict && <p className="rounded-xl bg-red-500/15 p-3 text-sm text-red-200">Paid after the slot hold expired and the slot was taken - please reschedule or refund.</p>}
        {whatsappMissing(b) && (
          <div className="flex flex-wrap items-start gap-2 rounded-xl border border-amber-300/50 bg-amber-300/15 p-3 text-sm text-amber-100" data-testid="whatsapp-missing-callout">
            <MessageSquareWarning className="mt-0.5 h-4 w-4 shrink-0 text-amber-300" />
            <span className="flex-1">The person has not confirmed to the doctor via WhatsApp yet.</span>
            <Button size="sm" data-testid="nudge-whatsapp-btn" onClick={() => nudgePatient(b)} className="h-7 rounded-full bg-[#25D366] px-3 text-xs text-[#06120e] hover:bg-[#3ee07c]">
              Nudge via WhatsApp
            </Button>
          </div>
        )}
        <dl className="grid grid-cols-[auto,1fr] gap-x-4 gap-y-1.5 text-sm">
          {rows.map(([k, v]) => (<Fragment key={k}><dt className="text-emerald-100/60">{k}</dt><dd className="break-words">{v}</dd></Fragment>))}
        </dl>
        {b.payment_proof && (showProof ? <ProofViewer id={b.id} /> : (
          <Button size="sm" variant="secondary" data-testid="view-proof-btn" onClick={() => setShowProof(true)} className="w-fit rounded-full">View payment screenshot</Button>
        ))}
        <div className="flex flex-wrap gap-2">
          {b.status === "payment_submitted" && <Button size="sm" data-testid="verify-upi-btn" onClick={() => update({ status: "confirmed" })} className="rounded-full bg-emerald-400 text-emerald-950">Mark payment completed</Button>}
          {b.status === "unpaid" && <Button size="sm" data-testid="mark-paid-btn" onClick={() => update({ status: "paid" })} className="rounded-full bg-emerald-400 text-emerald-950">Mark payment completed</Button>}
          {!b.consultation_completed && paymentDone && <Button size="sm" data-testid="mark-consultation-completed-btn" onClick={() => update({ consultation_completed: true, status: "completed" })} className="rounded-full bg-emerald-500 text-emerald-950"><FileText className="mr-1 h-4 w-4" /> Mark consultation completed</Button>}
          {canReceipt && <Button size="sm" data-testid="send-receipt-btn" onClick={sendReceipt} className="rounded-full bg-amber-300 text-emerald-950 hover:bg-amber-200"><Receipt className="mr-1 h-4 w-4" /> Send receipt</Button>}
          <Button size="sm" variant="destructive" data-testid="cancel-booking-btn" onClick={() => update({ status: "cancelled" })} className="rounded-full">Cancel booking</Button>
        </div>
        <Textarea data-testid="admin-note-input" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Private note" className="bg-black/20" />
        <Button size="sm" data-testid="save-note-btn" onClick={() => update({ admin_note: note })} className="w-fit rounded-full">Save note</Button>
      </DialogContent>
    </Dialog>
  );
}

export default function BookingsTab() {
  const call = useAdminApi();
  const [items, setItems] = useState(null);
  const [status, setStatus] = useState("all");
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(null);
  const [pending, setPending] = useState(0);
  const [version, setVersion] = useState(0);

  const loadList = useCallback(() => {
    call("get", "/admin/bookings", null, { params: { status: status === "all" ? undefined : status, q: q || undefined } }).then(setItems);
    call("get", "/admin/bookings", null, { params: { status: "payment_submitted" } }).then((l) => {
      setPending(l.length);
      if (l.length === 0) setStatus((s) => (s === "payment_submitted" ? "all" : s));
    });
  }, [call, status, q]);
  useEffect(() => {
    loadList();
  }, [loadList]);

  return (
    <div className="space-y-4" data-testid="admin-bookings">
      {pending > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-amber-300/50 bg-amber-300/15 p-4" data-testid="unverified-banner">
          <span className="flex items-center gap-2 text-sm text-amber-100">
            <AlertTriangle className="h-5 w-5 text-amber-300" />
            <b data-testid="unverified-count">{pending}</b> UPI payment{pending > 1 ? "s" : ""} waiting for your verification
          </span>
          <Button size="sm" data-testid="show-unverified-btn" onClick={() => setStatus(status === "payment_submitted" ? "all" : "payment_submitted")} className="rounded-full bg-amber-300 text-emerald-950 hover:bg-amber-200">
            {status === "payment_submitted" ? "Show all bookings" : "Show only these"}
          </Button>
        </div>
      )}
      <RemindersPanel />
      <div className="flex flex-wrap gap-2">
        <Input data-testid="bookings-search" placeholder="Search name, phone or ref" value={q} onChange={(e) => setQ(e.target.value)} className="max-w-xs bg-black/20" />
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-48 bg-black/20" data-testid="bookings-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {STATUSES.map((s) => <SelectItem key={s} value={s}>{LABEL[s]}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div className="overflow-x-auto">
        <Table>
          <TableHeader><TableRow><TableHead>Date</TableHead><TableHead>Name</TableHead><TableHead>Phone</TableHead><TableHead>Type</TableHead><TableHead>Rs</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
          <TableBody>
            {items?.map((b) => (
              <TableRow key={b.id} onClick={() => setOpen(b)} className={`cursor-pointer ${b.status === "payment_submitted" ? "shadow-[inset_4px_0_0_#fcd34d] bg-amber-300/10 hover:bg-amber-300/20" : b.status === "unpaid" ? "shadow-[inset_4px_0_0_#f87171] bg-red-500/10 hover:bg-red-500/20" : "hover:bg-white/5"}`} data-testid={`booking-row-${b.ref}`}>
                <TableCell className="whitespace-nowrap">{b.date} {b.time}</TableCell>
                <TableCell>{b.name}</TableCell>
                <TableCell>{b.phone}</TableCell>
                <TableCell className="max-w-[14rem] truncate">{b.service_label}</TableCell>
                <TableCell>{b.amount}</TableCell>
                <TableCell><Badge variant="outline" data-testid={`status-badge-${b.ref}`} className={`whitespace-nowrap ${b.status === "payment_submitted" ? "border-amber-300 bg-amber-300 text-emerald-950" : b.status === "unpaid" ? "border-red-300 bg-red-400 text-red-950" : "border-emerald-300/30 text-emerald-100"}`}>{b.status === "payment_submitted" ? "Not verified" : LABEL[b.status] || b.status}</Badge>{b.payment_proof && <ImageIcon className="ml-2 inline h-4 w-4 text-amber-200" data-testid={`proof-icon-${b.ref}`} />}{whatsappMissing(b) && <MessageSquareWarning title="Patient has not sent the confirmation WhatsApp yet" className="ml-2 inline h-4 w-4 text-amber-300" data-testid={`whatsapp-missing-icon-${b.ref}`} />}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {items?.length === 0 && <p className="py-8 text-center text-sm text-emerald-100/60" data-testid="bookings-empty">{status === "all" && !q ? "No bookings yet." : "No bookings match this filter."}</p>}
      </div>
      {open && <BookingDetail b={open} onClose={() => setOpen(null)} onSaved={(nb) => { setOpen(nb); loadList(); setVersion((v) => v + 1); }} />}
    </div>
  );
}
